import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, CircleDot, X } from 'lucide-react';
import type { DomainSyncStatus, IntegrationSyncStatus } from '@lizrd/contracts';
import { getSyncStatus } from '../lib/api';
import { ProviderLogo } from './ProviderLogo';
import { RegionsScanned } from './RegionsScanned';
import { Progress } from './ui/progress';
import { Spinner } from './ui/spinner';
import { Skeleton } from './ui/skeleton';
import { Stepper, type Step, type StepState } from './ui/stepper';

/** De-technicalize a raw stage/status token for customer eyes. */
const humanizeStage = (s: string): string => {
  const t = s.toUpperCase();
  if (t === 'IDLE') return 'queued';
  if (t === 'PENDING' || t === 'RUNNING' || t === 'CONVERGING') return 'building graph';
  return s.toLowerCase();
};

const ACTIVE_RUN = new Set(['QUEUED', 'RUNNING']);
const isActive = (s: IntegrationSyncStatus) =>
  s.status === 'SYNCING' || ACTIVE_RUN.has(s.runStatus ?? '');

const isFailed = (d: DomainSyncStatus): boolean =>
  d.integrations.some((s) => s.runStatus === 'FAILED') || d.graph.stage === 'FAILED';

// Every integration has a SUCCEEDED run (not merely "not currently syncing" — a
// connected-but-never-synced source must NOT count as done).
const allIntegrationsSucceeded = (d: DomainSyncStatus): boolean =>
  d.integrations.length > 0 && d.integrations.every((s) => s.runStatus === 'SUCCEEDED');

// A run is TERMINAL once it has SUCCEEDED or FAILED — a final verdict, not merely
// "not currently active".
const TERMINAL_RUN = new Set(['SUCCEEDED', 'FAILED']);
const allIntegrationsTerminal = (d: DomainSyncStatus): boolean =>
  d.integrations.length > 0 && d.integrations.every((s) => TERMINAL_RUN.has(s.runStatus ?? ''));
const anyIntegrationSucceeded = (d: DomainSyncStatus): boolean =>
  d.integrations.some((s) => s.runStatus === 'SUCCEEDED');
const graphTerminal = (d: DomainSyncStatus): boolean =>
  d.graph.stage === 'DONE' || d.graph.stage === 'FAILED';

// Settled = EVERY integration has reached a terminal run (succeeded OR failed), AND
// the knowledge-graph pass has finished too. The graph only runs when ≥1 integration
// produced data (it's scheduled after each successful sync), so if every source failed
// there's nothing to converge and we settle without it.
//
// Crucially we do NOT short-circuit the moment one source fails: a fast GitHub failure
// must not stop polling and freeze a still-running AWS sync at a stale frame. A partial
// result (some sources ✓, some ✕) settles here and is reflected per integration — the
// pipeline never hangs on a single failure.
export const isSettled = (d: DomainSyncStatus): boolean =>
  allIntegrationsTerminal(d) && (anyIntegrationSucceeded(d) ? graphTerminal(d) : true);

// The four stages of a domain sync. Discovery + extraction run per integration;
// the knowledge-graph convergence runs ONCE after they settle.
const STEP_DEFS = [
  { label: 'Trigger sync', description: 'Queue a fresh sync for every connected integration.' },
  { label: 'Sync integrations', description: 'Pull live resources from each connected source.' },
  {
    label: 'Extract identities',
    description: 'Parse code, IaC and container images for identifiers.',
  },
  { label: 'Update knowledge graph', description: 'Merge resources and link code ↔ cloud — once.' },
] as const;

/**
 * Live domain-sync pipeline (KG cockpit): a numbered stage sequence plus a
 * per-integration progress list. Polls status and calls `onSettled` exactly once
 * when the whole sync reaches a terminal state (every integration succeeded and
 * the graph converged, or something failed) — never merely because nothing is
 * momentarily active.
 */
export function SyncPipeline({
  envId,
  active,
  onSettled,
  onClose,
}: {
  envId: string;
  active: boolean;
  onSettled: () => void;
  onClose?: () => void;
}) {
  const status = useQuery({
    queryKey: ['sync-status', envId],
    queryFn: () => getSyncStatus(envId),
    enabled: active,
    refetchInterval: (q) => (q.state.data && !isSettled(q.state.data) ? 1500 : false),
  });

  const settledRef = useRef(false);
  useEffect(() => {
    if (!status.data) return;
    if (isSettled(status.data)) {
      if (!settledRef.current) {
        settledRef.current = true;
        onSettled();
      }
    } else {
      settledRef.current = false;
    }
  }, [status.data, onSettled]);

  // A live elapsed counter so a long sync never looks frozen — motion the user
  // can see even when a single stage is quietly grinding for minutes. Stops once
  // the sync settles (tracked via `done` below gating the tick).
  const [elapsed, setElapsed] = useState(0);
  const done = !!status.data && isSettled(status.data);
  useEffect(() => {
    if (!active || done) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [active, done]);
  const elapsedLabel =
    elapsed >= 60 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : `${elapsed}s`;

  if (!active) return null;
  // Skeleton the first frame so the pipeline doesn't pop in (layout jump) before
  // the initial status resolves.
  if (!status.data) {
    return (
      <div className="mt-1 space-y-3 rounded-md border border-hairline bg-surface-2/50 p-4">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-2.5 w-full rounded-full" />
      </div>
    );
  }
  const rows = status.data.integrations;
  const graphStage = status.data.graph.stage;

  const intActive = rows.some(isActive);
  const intSucceeded = allIntegrationsSucceeded(status.data);
  const intFailed = rows.some((s) => s.runStatus === 'FAILED');
  const succeededCount = rows.filter((s) => s.runStatus === 'SUCCEEDED').length;
  const avg = rows.length ? rows.reduce((sum, s) => sum + s.progressPct, 0) / rows.length : 0;

  const anyFailed = isFailed(status.data);
  const success = isSettled(status.data) && !anyFailed;
  // Busy until we're settled or failed — including the "triggered but not started"
  // gap and the "integrations done, graph still converging" tail.
  const busy = !success && !anyFailed;

  const graphStep: StepState =
    graphStage === 'DONE'
      ? 'done'
      : graphStage === 'FAILED'
        ? 'error'
        : graphStage === 'RUNNING'
          ? 'active'
          : // Integrations succeeded and the graph pass is scheduled (PENDING) or
            // about to be (IDLE): it's in progress, not done.
            intSucceeded
            ? 'active'
            : 'pending';

  const steps: Step[] = [
    { ...STEP_DEFS[0], state: 'done' },
    {
      ...STEP_DEFS[1],
      state: intSucceeded ? 'done' : intFailed ? 'error' : 'active',
      hint: rows.length ? `${succeededCount}/${rows.length}` : undefined,
    },
    {
      ...STEP_DEFS[2],
      state: intSucceeded ? 'done' : intActive && avg >= 55 ? 'active' : 'pending',
    },
    { ...STEP_DEFS[3], state: graphStep },
  ];

  // One glance-able aggregate: the integration pass carries most of the bar, the
  // knowledge-graph pass the last slice — so "how far along am I?" reads at once.
  const overallPct = success
    ? 100
    : rows.length === 0
      ? 0
      : Math.round(avg * 0.85 + (graphStep === 'done' ? 15 : graphStep === 'active' ? 8 : 0));

  return (
    <div className="mt-1 rounded-md border border-hairline bg-surface-2/50 p-4">
      {/* Aggregate hero progress — the single number that shows motion. */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-bone">
            {success
              ? 'Domain synced'
              : anyFailed
                ? 'Sync finished with errors'
                : rows.length
                  ? `${succeededCount} of ${rows.length} source${rows.length === 1 ? '' : 's'} synced`
                  : 'Starting sync…'}
          </span>
          <div className="flex items-center gap-3">
            {busy && (
              <span className="tnum text-xs text-muted" aria-label="Time elapsed">
                {elapsedLabel} · safe to leave
              </span>
            )}
            {!busy && onClose && (
              <button
                onClick={onClose}
                className="rounded-sm px-2 py-0.5 text-xs text-muted transition-colors hover:bg-surface-3 hover:text-bone"
              >
                Close
              </button>
            )}
          </div>
        </div>
        <Progress
          value={overallPct}
          size="lg"
          variant={anyFailed ? 'danger' : success ? 'success' : 'iris'}
        />
      </div>

      {/* Mechanics demoted into an opt-in detail so the payoff leads. */}
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-semibold text-muted transition-colors hover:text-bone [&::-webkit-details-marker]:hidden">
          <CircleDot
            className="h-3.5 w-3.5 transition-transform group-open:rotate-90"
            aria-hidden
          />
          Sync details
        </summary>

        <div className="mt-3 grid gap-5 md:grid-cols-2">
          <Stepper steps={steps} />

          <div className="space-y-3">
            <h4 className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
              Integrations
            </h4>
            {rows.map((s) => {
              const failed = s.runStatus === 'FAILED';
              const doneRow = s.runStatus === 'SUCCEEDED';
              const pct = isActive(s) ? Math.max(5, s.progressPct) : doneRow ? 100 : s.progressPct;
              return (
                <div key={s.integrationId} className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    {isActive(s) ? (
                      <Spinner size="sm" className="h-3.5 w-3.5" />
                    ) : doneRow ? (
                      <Check className="h-3.5 w-3.5 text-iris" aria-hidden />
                    ) : failed ? (
                      <X className="h-3.5 w-3.5 text-danger" aria-hidden />
                    ) : (
                      <CircleDot className="h-3.5 w-3.5 text-muted" aria-hidden />
                    )}
                    <ProviderLogo provider={s.provider} size={14} />
                    <span className="truncate text-bone">{s.displayName}</span>
                    <RegionsScanned
                      regions={s.regions}
                      className="shrink-0 text-[0.65rem] text-muted"
                    />
                    <span className="ml-auto shrink-0 text-muted">
                      {s.stage ?? humanizeStage(s.status)}
                    </span>
                  </div>
                  <Progress
                    value={pct}
                    size="sm"
                    variant={failed ? 'danger' : doneRow ? 'success' : 'iris'}
                  />
                </div>
              );
            })}
            <div className="flex items-center gap-2 border-t border-hairline pt-2 text-xs">
              {graphStep === 'active' ? (
                <Spinner size="sm" className="h-3.5 w-3.5" />
              ) : graphStep === 'error' ? (
                <X className="h-3.5 w-3.5 text-danger" aria-hidden />
              ) : graphStep === 'done' ? (
                <Check className="h-3.5 w-3.5 text-iris" aria-hidden />
              ) : (
                <CircleDot className="h-3.5 w-3.5 text-muted" aria-hidden />
              )}
              <span className="text-bone">Knowledge graph</span>
              <span className="ml-auto text-muted">
                {graphStage === 'IDLE' && intSucceeded
                  ? 'building graph'
                  : humanizeStage(graphStage)}
              </span>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
}
